import { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { getListProductsByHost, searchProductsByHostAction } from '_redux/modules/product'
import { PRODUCTS_PAGE_SIZE } from '_utils/constant'
import usePagination from '../hooks/usePagination'

export default function ProductPagination({
  groupId,
  searchKeyword,
  filterKey,
  itemsLength,
  lastProductTimestamp,
  lastProductId,
  hasMore,
  onPageChange,
}) {
  const dispatch = useDispatch()

  const onLoadMore = useCallback(() => {
    const cursorParams = { lastProductTimestamp, lastProductId }
    if (searchKeyword) {
      return dispatch(
        searchProductsByHostAction({ groupId, searchString: searchKeyword, ...cursorParams })
      )
    }
    return dispatch(getListProductsByHost({ groupId, ...cursorParams }))
  }, [dispatch, groupId, searchKeyword, lastProductTimestamp, lastProductId])

  const {
    currentPage,
    totalPages,
    pageRange,
    handleNext,
    handlePrev,
    handlePageClick,
    getPageNumbers,
  } = usePagination({
    resetDependencies: [groupId, filterKey],
    itemsLength,
    pageSize: PRODUCTS_PAGE_SIZE,
    hasMore,
    onLoadMore,
  })

  useEffect(() => {
    onPageChange?.(pageRange)
  }, [pageRange, onPageChange])

  const scrollToTop = () => {
    const container = document.getElementById('products-items')
    if (container) {
      container.scrollIntoView({ behavior: 'smooth' })
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const { pages, showLeftEllipsis, showRightEllipsis } = getPageNumbers()

  return (
    <nav>
      <ul className='product-pagination'>
        {/* Prev Button */}
        <li
          className={`prev ${currentPage <= 1 ? 'disabled' : ''}`}
          onClick={() => {
            if (currentPage > 1) {
              handlePrev()
              scrollToTop()
            }
          }}
        >
          ‹
        </li>

        {/* Left Ellipsis */}
        {showLeftEllipsis && <li className='dots'>...</li>}

        {/* Page Numbers */}
        {pages.map((page) => {
          const isDisabled = page > totalPages && page !== currentPage
          return (
            <li
              key={page}
              className={`${page === currentPage ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => {
                if (!isDisabled) {
                  handlePageClick(page)
                  scrollToTop()
                }
              }}
            >
              {page}
            </li>
          )
        })}

        {/* Right Ellipsis */}
        {showRightEllipsis && <li className='dots'>...</li>}

        {/* Next Button - Disabled when no more pages */}
        <li
          className={`next ${currentPage >= totalPages && !hasMore ? 'disabled' : ''}`}
          onClick={() => {
            if (currentPage < totalPages || hasMore) {
              handleNext()
              scrollToTop()
            }
          }}
        >
          ›
        </li>
      </ul>
    </nav>
  )
}
